/**
 * BrandingSettings.tsx — T321 SETTINGS-1 / FR-004 rewrite.
 *
 * Wires the four branding fields to
 *   - api.organizations.getBranding  (read; honest defaults when no row)
 *   - api.organizations.updateBranding (admin-only mutation; read-merge-write
 *                                       into systemConfig:branding)
 *
 * Honest-state contract: every Save is routed through useMutationWithToast
 * (FR-013 (H1)). The wrapper logs argument KEYS only (never values),
 * re-throws after toasting, and never fires toast.success unless the
 * mutation actually resolved on the server (FR-013 (H2), FR-004 (S3)).
 *
 * H4 ratchet: this file is no longer the thin re-export shell — the
 * standalone body satisfies the per-sub-page S2 test, and the wired
 * mutation satisfies the per-sub-page S3/S5 tests in
 * frontend-v3/tests/destub/honest-state.test.ts (FR-004).
 */
import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import {
  Btn,
  Field,
  Input,
  Panel,
  SegmentedControl,
} from '../../components/primitives';
import { useMutationWithToast } from '../../hooks/useMutationWithToast';
import { api } from '@convex/api';

interface BrandingDoc {
  logoUrl: string | null;
  primaryColor: string | null;
  accentColor: string | null;
  typeface: string | null;
}

export function BrandingSettings() {
  const current = useQuery(api.organizations.getBranding) as
    | BrandingDoc
    | undefined;

  const updateBrandingM = useMutationWithToast(
    api.organizations.updateBranding,
    {
      logLabel: 'organizations_updateBranding',
      successMessage: 'Marca actualizada.',
      errorMessagePrefix: 'No se pudo actualizar la marca',
    },
  );

  const [logoUrl, setLogoUrl] = useState('');
  const [primaryColor, setPrimaryColor] = useState('');
  const [typeface, setTypeface] = useState<'default' | 'oscuro' | 'claro'>(
    'claro',
  );

  // Seed local form state from the server document once it loads.
  useEffect(() => {
    if (!current) return;
    if (typeof current.logoUrl === 'string') setLogoUrl(current.logoUrl);
    if (typeof current.primaryColor === 'string') {
      setPrimaryColor(current.primaryColor);
    }
    if (
      current.typeface === 'default' ||
      current.typeface === 'oscuro' ||
      current.typeface === 'claro'
    ) {
      setTypeface(current.typeface);
    }
  }, [current]);

  const handleSave = () => {
    void updateBrandingM.run({
      logoUrl,
      primaryColor,
      typeface,
    });
  };

  const isSaving = updateBrandingM.loading;
  const isLoading = current === undefined;

  return (
    <Panel title="Marca" pad={28}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <Field label="Logotipo (URL)">
          <Input
            placeholder="https://…"
            value={logoUrl}
            onChange={setLogoUrl}
            disabled={isLoading || isSaving}
          />
        </Field>
        <Field label="Color de marca">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <span
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: primaryColor || 'var(--deep-green)',
              }}
            />
            <Input
              value={primaryColor || '#003c33'}
              onChange={setPrimaryColor}
              full={false}
              style={{ width: 140 }}
              disabled={isLoading || isSaving}
            />
          </div>
        </Field>
        <Field label="Skin del reproductor">
          <SegmentedControl
            value={typeface}
            onChange={(v: 'default' | 'oscuro' | 'claro') => setTypeface(v)}
            options={[
              { value: 'default', label: 'Por defecto' },
              { value: 'oscuro', label: 'Oscuro' },
              { value: 'claro', label: 'Claro' },
            ]}
          />
        </Field>
        <div>
          <Btn
            variant="solid"
            onClick={handleSave}
            disabled={isLoading || isSaving}
          >
            {isSaving ? 'Guardando…' : 'Guardar'}
          </Btn>
        </div>
      </div>
    </Panel>
  );
}

export default BrandingSettings;
